import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import type { Member } from '@stytch/vanilla-js'
import { MoreHorizontal, Plus, Users } from 'lucide-react'

interface UsersTableProps {
	members: Member[]
	onSelectUser: (member: Member) => void
	onAddUser: () => void
	selectedUserId?: string
}

export function UsersTable({ members, onSelectUser, onAddUser, selectedUserId }: UsersTableProps) {
	return (
		<Table aria-label="Users">
			<TableHeader className="border-t border-t-[#3A2E2F]/20 border-b border-b-[#3A2E2F] [&_tr]:!border-0">
				<TableRow>
					<TableHead>Name</TableHead>
					<TableHead>Role</TableHead>
					<TableHead>Status</TableHead>
					<TableHead className="w-[50px]"></TableHead>
				</TableRow>
			</TableHeader>
			<TableBody className="[&_tr:last-child]:!border-b [&_tr]:!border-0">
				{members.length === 0 ? (
					<TableRow className="!border-b !border-b-[#3A2E2F]/20">
						<TableCell colSpan={4} className="h-32 p-0">
							<div className="flex flex-col items-center justify-center text-center space-y-3 bg-[#3A2E2F]/5 h-full py-8">
								<Users className="h-8 w-8 text-foreground/60" />
								<p className="text-sm font-medium text-muted-foreground">No users yet</p>
								<Button variant="outline" size="sm" onClick={onAddUser}>
									<Plus className="h-4 w-4 mr-2" />
									Add User
								</Button>
							</div>
						</TableCell>
					</TableRow>
				) : (
					members.map((member) => (
						<TableRow
							key={member.member_id}
							className={`group cursor-pointer hover:bg-muted/50 !border-b !border-b-[#3A2E2F]/20 ${
								selectedUserId === member.member_id ? 'bg-muted' : ''
							}`}
						onClick={() => onSelectUser(member)}
					>
						<TableCell>
							<div className="flex items-center gap-3">
								<Avatar className="h-8 w-8">
									<AvatarImage src="" alt={member.name || member.email_address} />
									<AvatarFallback>
										{(() => {
											const text = member.name || member.email_address
											const parts = text.split(' ')
											return parts.length > 1
												? parts.slice(0, 2).map((n) => n[0]).join('').toUpperCase()
												: text.slice(0, 2).toUpperCase()
										})()}
									</AvatarFallback>
								</Avatar>
								<div>
									<div className="font-medium">{member.name || 'Unnamed User'}</div>
									<div className="text-sm text-muted-foreground">{member.email_address}</div>
								</div>
							</div>
						</TableCell>
						<TableCell>
							<Badge variant="secondary" className="capitalize">
								{member.roles?.[0]?.role_id || 'member'}
							</Badge>
						</TableCell>
						<TableCell>
							<Badge variant={member.status === 'active' ? 'default' : 'secondary'}>
								{member.status}
							</Badge>
						</TableCell>
						<TableCell>
							<Button
								variant="ghost"
								size="icon"
								className="opacity-0 group-hover:opacity-100 transition-opacity"
								aria-label={`More actions for ${member.name || member.email_address}`}
								onClick={(e) => {
									e.stopPropagation()
									onSelectUser(member)
								}}
							>
								<MoreHorizontal className="h-4 w-4" />
							</Button>
						</TableCell>
					</TableRow>
					))
				)}
			</TableBody>
		</Table>
	)
}
